import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, Clock } from 'lucide-react';
import { ticketsApi } from '../../api/tickets';
import { tasksApi } from '../../api/tasks';
import { crmStatsApi, crmWorkApi } from '../../api/crm';
import { useAuth } from '../../contexts/AuthContext';
import { useTicketStatuses, ACTIVE_CATEGORY_CSV } from '../../hooks/useTicketStatuses';
import { useClassifications } from '../../hooks/useClassifications';
import { StatusBadge, PriorityBadge, cssColor } from '../badges';
import { DraftMark, WaitingOnYouMark, TaskForYouMark, taskForYouTitleStyle } from '../tickets/TicketRowMarks';
import BarBreakdown from './BarBreakdown';
import type { Ticket } from '../../types';

// ─────────────────────────────────────────────────────────────
// The widget catalogue. Each entry declares its config options so the editor
// can render a form without knowing anything about the widget, and the server
// stores `type` as an opaque string — a type retired here degrades to a
// "missing widget" tile rather than corrupting a saved layout.
// ─────────────────────────────────────────────────────────────

export interface WidgetOption {
  key: string;
  label: string;
  choices: { value: string; label: string }[];
  default: string;
}

export interface WidgetDef {
  type: string;
  name: string;
  description: string;
  /** Columns of 12 when first added. */
  defaultWidth: number;
  /** Narrowest useful span — below this the widget stops being readable. */
  minWidth: number;
  /** Pinned height in px when first added; omit to size to content. */
  defaultHeight?: number;
  options: WidgetOption[];
  /** Title shown on the tile, derived from config so it reads specifically. */
  titleOf: (config: Record<string, any>) => string;
  Component: React.ComponentType<{ config: Record<string, any> }>;
}

const ticketId = (t: any) => t.id ?? t._id;

function pick(config: Record<string, any>, opt: WidgetOption): string {
  const v = config?.[opt.key];
  return opt.choices.some(c => c.value === v) ? v : opt.default;
}

// ─── Stat tile ────────────────────────────────────────────────
// A single headline number: no plot, so no chart and no hover layer.

const STAT_METRICS = [
  { value: 'open', label: 'Open tickets' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High priority' },
  { value: 'sla', label: 'SLA breached' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'mine', label: 'Assigned to me' },
  { value: 'waiting', label: 'Waiting on you' },
  { value: 'tasks', label: 'Tasks for you' },
];

const STAT_OPTION: WidgetOption = {
  key: 'metric', label: 'Metric', choices: STAT_METRICS, default: 'open',
};

function StatWidget({ config }: { config: Record<string, any> }) {
  const metric = pick(config, STAT_OPTION);
  const { user } = useAuth();
  const myId = (user as any)?.id;

  const { data: stats } = useQuery({
    queryKey: ['ticket-stats'],
    queryFn: () => ticketsApi.getStats(),
  });

  // "Unassigned", "mine" and "waiting" aren't in the stats summary — count
  // them directly. The waiting count shares the Home heading's query key so
  // the two never disagree.
  const needsList = metric === 'unassigned' || metric === 'mine' || metric === 'waiting';
  const { data: list } = useQuery({
    queryKey: metric === 'waiting' ? ['tickets', 'waiting-on-me'] : ['dash-stat-list', metric, myId],
    queryFn: () => ticketsApi.list(
      metric === 'waiting'
        ? { waitingOnMe: '1', limit: 1 }
        : {
            statusCategory: ACTIVE_CATEGORY_CSV,
            ...(metric === 'mine' && myId ? { assignedTo: myId } : {}),
            limit: 1,
          },
    ),
    enabled: needsList && (metric !== 'mine' || !!myId),
  });
  // Open sub-ticket tasks assigned to you — shares the Home heading's key.
  const { data: myTasks } = useQuery({
    queryKey: ['tasks', 'mine-count'],
    queryFn: () => tasksApi.mine('open'),
    enabled: metric === 'tasks',
  });

  const byCategory = (stats as any)?.byCategory ?? [];
  const byPriority = (stats as any)?.byPriority ?? [];
  const catCount = (c: string) => byCategory.find((x: any) => x.statusCategory === c)?._count ?? 0;
  const prioCount = (p: string) => byPriority.find((x: any) => x.priority === p)?._count ?? 0;
  const openTotal = byCategory
    .filter((s: any) => !['resolved', 'closed'].includes(s.statusCategory))
    .reduce((a: number, s: any) => a + (s._count ?? 0), 0);

  const spec: Record<string, { value: number; tone: string; to: string }> = {
    open:       { value: openTotal,                 tone: 'var(--scpa-primary)',      to: '/tickets' },
    critical:   { value: prioCount('critical'),     tone: 'var(--scpa-status-error)', to: '/tickets?priority=critical' },
    high:       { value: prioCount('high'),         tone: 'var(--scpa-status-warning)', to: '/tickets?priority=high' },
    sla:        { value: (stats as any)?.slaBreached ?? 0, tone: 'var(--scpa-status-error)', to: '/tickets' },
    resolved:   { value: catCount('resolved'),      tone: 'var(--scpa-status-ok)',    to: '/tickets?statusCategory=resolved' },
    unassigned: { value: (list as any)?.total ?? 0, tone: 'var(--scpa-text-muted)',   to: '/tickets' },
    mine:       { value: (list as any)?.total ?? 0, tone: 'var(--scpa-primary)',      to: myId ? `/tickets?assignedTo=${myId}` : '/tickets' },
    waiting:    { value: (list as any)?.total ?? 0, tone: 'var(--scpa-status-warning)', to: '/tickets?waitingOnMe=1' },
    tasks:      { value: myTasks?.openCount ?? 0,   tone: 'var(--scpa-primary)',      to: '/tickets?taskForMe=1' },
  };
  const s = spec[metric] ?? spec.open;
  const label = STAT_METRICS.find(m => m.value === metric)?.label ?? 'Open';

  return (
    <Link to={s.to} className="flex flex-col justify-center h-full gap-1 px-1 py-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</span>
      <span className="text-3xl font-bold tabular-nums" style={{ color: s.tone }}>{s.value}</span>
    </Link>
  );
}

// ─── Ticket list ──────────────────────────────────────────────

const LIST_PRESETS = [
  { value: 'mine', label: 'Assigned to me' },
  { value: 'aged', label: 'Oldest open' },
  { value: 'recent', label: 'Recently updated' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'critical', label: 'Critical & high' },
  { value: 'resolved', label: 'Recently resolved' },
  { value: 'waiting', label: 'Waiting on you' },
  { value: 'drafts', label: 'My drafts' },
  { value: 'tasks', label: 'Tasks for you' },
];

const LIST_OPTIONS: WidgetOption[] = [
  { key: 'preset', label: 'Show', choices: LIST_PRESETS, default: 'mine' },
  {
    // The body scrolls, so a high row count costs height inside the widget
    // rather than page space — worth offering more than a screenful.
    key: 'limit', label: 'Rows',
    choices: [5, 10, 25, 50, 100].map(n => ({ value: String(n), label: `${n} rows` })),
    default: '25',
  },
];

function daysOld(iso: string) {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/** Compact relative time for the Updated column ("3m", "2h", "5d", else "Aug 27"). */
function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function TicketListWidget({ config }: { config: Record<string, any> }) {
  const preset = pick(config, LIST_OPTIONS[0]);
  const limit = Number(pick(config, LIST_OPTIONS[1]));
  // Header-click sort for the "Assigned to me" Updated column. Server-side sort,
  // so the direction has to reach the query — not just reorder the page shown.
  const [updatedSort, setUpdatedSort] = useState<'desc' | 'asc'>('desc');
  const { user } = useAuth();
  const myId = (user as any)?.id;
  useTicketStatuses();   // feeds the badge registry with custom labels/colors

  const filters: Record<string, any> = { limit };
  if (preset === 'mine') Object.assign(filters, { assignedTo: myId, statusCategory: ACTIVE_CATEGORY_CSV, sort: `updatedAt_${updatedSort}` });
  if (preset === 'aged') Object.assign(filters, { statusCategory: ACTIVE_CATEGORY_CSV, sort: 'createdAt_asc' });
  if (preset === 'recent') Object.assign(filters, { statusCategory: ACTIVE_CATEGORY_CSV, sort: 'updatedAt_desc' });
  if (preset === 'unassigned') Object.assign(filters, { statusCategory: ACTIVE_CATEGORY_CSV, sort: 'createdAt_asc' });
  if (preset === 'critical') Object.assign(filters, { priority: 'critical,high', statusCategory: ACTIVE_CATEGORY_CSV, sort: 'createdAt_asc' });
  if (preset === 'resolved') Object.assign(filters, { statusCategory: 'resolved,closed', sort: 'updatedAt_desc' });
  if (preset === 'waiting') Object.assign(filters, { waitingOnMe: '1', sort: 'updatedAt_desc' });
  // Drafts can sit on any ticket, resolved ones included — no status filter.
  if (preset === 'drafts') Object.assign(filters, { draft: 'mine', sort: 'updatedAt_desc' });
  // Tickets with an open sub-ticket task assigned to you.
  if (preset === 'tasks') Object.assign(filters, { taskForMe: '1', sort: 'updatedAt_desc' });

  const { data, isLoading } = useQuery({
    queryKey: ['dash-list', preset, limit, myId, preset === 'mine' ? updatedSort : ''],
    queryFn: () => ticketsApi.list(filters),
    enabled: preset !== 'mine' || !!myId,
  });

  // The API has no "unassigned" filter, so narrow client-side. The count shown
  // is therefore of this page, not the whole set — no total is displayed.
  const all = (data?.tickets ?? []) as Ticket[];
  const tickets = preset === 'unassigned'
    ? all.filter((t: any) => !t.assignedToId && !t.assignedTo)
    : all;

  // The sort header must survive a re-query (toggling the direction changes the
  // query key, which flashes the loading state), so the states render as a body
  // below it rather than early-returning past it.
  const body = isLoading
    ? <p className="text-[12px] text-muted py-4 text-center">Loading…</p>
    : tickets.length === 0
      ? <p className="text-[12px] text-muted py-4 text-center">Nothing here right now.</p>
      : (
        <ul className="divide-y divide-line-light -mx-1">
          {tickets.map((t: any) => (
            <li key={ticketId(t)}>
              <Link
                to={`/tickets/${ticketId(t)}`}
                className="flex items-center gap-2 px-1 py-2 hover:bg-row-odd transition-colors rounded"
              >
                <span className="font-mono font-bold text-primary text-[12px] shrink-0">{t.ticketNumber}</span>
                <span
                  className={`flex-1 min-w-0 truncate text-[12px] ${(t.tasksForYou?.length ?? 0) > 0 ? 'font-bold' : 'text-fg'}`}
                  style={taskForYouTitleStyle(t.tasksForYou)}
                  title={(t.tasksForYou?.length ?? 0) > 0 ? `Your tasks: ${t.tasksForYou.map((x: any) => x.title).join(', ')}` : undefined}
                >
                  {t.title}
                </span>
                {t.waitingOnYou && preset !== 'waiting' && <WaitingOnYouMark compact />}
                <TaskForYouMark tasks={t.tasksForYou} />
                <DraftMark mine={t.hasMyDraft} others={t.draftsBy} />
                <StatusBadge status={t.status} />
                <PriorityBadge priority={t.priority} />
                {preset === 'aged' && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted shrink-0">
                    <Clock className="w-3 h-3" /> {daysOld(t.createdAt)}d
                  </span>
                )}
                {preset === 'mine' && (
                  <span className="text-[11px] font-semibold tabular-nums text-muted shrink-0 w-12 text-right">
                    {timeAgo(t.updatedAt)}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      );

  if (preset !== 'mine') return body;

  return (
    <>
      <div className="flex justify-end border-b border-line-light -mx-1 px-1 pb-1">
        <button
          type="button"
          onClick={() => setUpdatedSort(s => (s === 'desc' ? 'asc' : 'desc'))}
          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted hover:text-fg transition-colors"
          title={updatedSort === 'desc' ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}
        >
          Updated
          {updatedSort === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
        </button>
      </div>
      {body}
    </>
  );
}

// ─── Breakdown ────────────────────────────────────────────────

const BREAKDOWN_OPTION: WidgetOption = {
  key: 'by', label: 'Break down by',
  choices: [
    { value: 'status', label: 'Status' },
    { value: 'priority', label: 'Priority' },
  ],
  default: 'status',
};

const PRIORITY_TONE: Record<string, string> = {
  critical: 'var(--scpa-status-error)',
  high: 'var(--scpa-status-warning)',
  normal: 'var(--scpa-primary)',
  low: 'var(--scpa-text-muted)',
};

function BreakdownWidget({ config }: { config: Record<string, any> }) {
  const by = pick(config, BREAKDOWN_OPTION);
  const { statuses } = useTicketStatuses();
  const priorities = useClassifications().byKind('priority');

  const { data: stats } = useQuery({
    queryKey: ['ticket-stats'],
    queryFn: () => ticketsApi.getStats(),
  });

  if (by === 'priority') {
    // The company's own priorities, in their configured order and colors —
    // the same treatment the status breakdown below gets, so a priority a
    // tenant added shows up here instead of being silently dropped.
    const byPriority = ((stats as any)?.byPriority ?? []) as any[];
    const rows = priorities
      .map(p => ({
        label: p.label,
        value: byPriority.find(x => x.priority === p.key)?._count ?? 0,
        color: cssColor(p.color) || PRIORITY_TONE[p.key],
        to: `/tickets?priority=${p.key}`,
      }))
      .filter(r => r.value > 0);
    return <BarBreakdown rows={rows} emptyText="No tickets yet." />;
  }

  // By status: the company's own statuses, in their configured order, using
  // the same colors their badges use elsewhere.
  const byStatus = ((stats as any)?.byStatus ?? []) as any[];
  const rows = statuses
    .filter(s => s.isActive)
    .map(s => ({
      label: s.label,
      value: byStatus.find(x => x.status === s.key)?._count ?? 0,
      color: cssColor(s.color),
      to: `/tickets?status=${encodeURIComponent(s.key)}`,
    }))
    .filter(r => r.value > 0);
  return <BarBreakdown rows={rows} emptyText="No tickets yet." />;
}

// ─── Registry ─────────────────────────────────────────────────


// ─── CRM widgets ──────────────────────────────────────────────
// The CRM's two questions on the home dashboard: what needs answering, and
// what is the pipeline worth. Both read the same endpoints the CRM screens
// use, so a number here and a number there can never disagree.

const CRM_METRICS = [
  { value: 'needsReply', label: 'Conversations needing a reply' },
  { value: 'overdueReply', label: 'Waiting over a day' },
  { value: 'unassignedConv', label: 'Unassigned conversations' },
  { value: 'openDeals', label: 'Open opportunities' },
  { value: 'pipelineValue', label: 'Pipeline value' },
  { value: 'wonValue', label: 'Won (30 days)' },
  { value: 'newLeads', label: 'New leads (30 days)' },
  { value: 'firstResponse', label: 'Average first response' },
];

const CRM_METRIC_OPTION: WidgetOption = {
  key: 'metric', label: 'Metric', choices: CRM_METRICS, default: 'needsReply',
};

function CrmStatWidget({ config }: { config: Record<string, any> }) {
  const metric = pick(config, CRM_METRIC_OPTION);
  const { data } = useQuery({ queryKey: ['crm-stats', 30], queryFn: () => crmStatsApi.get(30) });

  const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
  const hours = (h: number | null) =>
    h === null ? '—' : h < 1 ? `${Math.max(1, Math.round(h * 60))}m` : h < 48 ? `${Math.round(h * 10) / 10}h` : `${Math.round((h / 24) * 10) / 10}d`;

  const view =
    metric === 'needsReply' ? { value: data?.inbox.needsReply ?? 0, sub: 'they wrote last', to: '/crm/inbox', tone: '' }
    : metric === 'overdueReply' ? { value: data?.inbox.overdue ?? 0, sub: `over ${data?.inbox.overdueAfterHours ?? 24}h`, to: '/crm/inbox', tone: (data?.inbox.overdue ?? 0) > 0 ? 'text-err' : '' }
    : metric === 'unassignedConv' ? { value: data?.inbox.open ?? 0, sub: 'open in the inbox', to: '/crm/inbox', tone: '' }
    : metric === 'openDeals' ? { value: data?.deals.open ?? 0, sub: money(data?.deals.openValue ?? 0), to: '/crm/deals/board', tone: '' }
    : metric === 'pipelineValue' ? { value: money(data?.deals.openValue ?? 0), sub: `${data?.deals.open ?? 0} open`, to: '/crm/deals/board', tone: '' }
    : metric === 'wonValue' ? { value: money(data?.deals.wonValueInPeriod ?? 0), sub: `${data?.deals.wonInPeriod ?? 0} won`, to: '/crm', tone: '' }
    : metric === 'newLeads' ? { value: data?.leads.newContacts ?? 0, sub: 'last 30 days', to: '/crm/contacts', tone: '' }
    : { value: hours(data?.inbox.avgFirstResponseHours ?? null), sub: 'average, 30 days', to: '/crm', tone: '' };

  return (
    <Link to={view.to} className="flex flex-col justify-center h-full px-1 group">
      <span className={`text-3xl font-bold ${view.tone || 'text-fg'}`}>{view.value}</span>
      <span className="text-xs text-muted mt-1 group-hover:text-primary transition-colors">{view.sub}</span>
    </Link>
  );
}

/** The work queue, on the dashboard: what is due, late or undecided. */
function CrmWorkWidget({ config }: { config: Record<string, any> }) {
  const mine = pick(config, CRM_WORK_SCOPE) === 'mine';
  const { data } = useQuery({ queryKey: ['crm-work-counts', mine], queryFn: () => crmWorkApi.counts(mine) });

  const rows: { label: string; n: number; to: string; urgent?: boolean }[] = [
    { label: 'Overdue', n: data?.overdue ?? 0, to: '/crm/work', urgent: true },
    { label: 'Due today', n: data?.today ?? 0, to: '/crm/work' },
    { label: 'This week', n: data?.upcoming ?? 0, to: '/crm/work' },
    { label: 'Next step due', n: data?.nextDue ?? 0, to: '/crm/work', urgent: true },
    { label: 'No next step', n: data?.noNextAction ?? 0, to: '/crm/work' },
    { label: 'Gone quiet', n: data?.stale ?? 0, to: '/crm/work' },
  ];

  return (
    <ul className="divide-y divide-line">
      {rows.map(r => (
        <li key={r.label}>
          <Link to={r.to} className="flex items-center justify-between py-2 hover:bg-row-odd -mx-2 px-2 rounded transition-colors">
            <span className="text-sm text-fg">{r.label}</span>
            <span className={`text-sm font-semibold ${r.urgent && r.n > 0 ? 'text-err' : 'text-muted'}`}>{r.n}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

const CRM_WORK_SCOPE: WidgetOption = {
  key: 'scope', label: 'Whose work', default: 'mine',
  choices: [{ value: 'mine', label: 'Mine' }, { value: 'all', label: 'Everyone' }],
};

/** Open opportunities by stage, with what each column is worth. */
function CrmPipelineWidget() {
  const { data } = useQuery({ queryKey: ['crm-stats', 30], queryFn: () => crmStatsApi.get(30) });
  const stages = data?.deals.byStage ?? [];
  const max = Math.max(1, ...stages.map(s => s.count));

  if (stages.length === 0) {
    return <p className="text-sm text-muted py-4 text-center">No open opportunities.</p>;
  }
  return (
    <div className="space-y-2">
      {stages.map(s => (
        <div key={s.stageId} className="text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-fg truncate flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />{s.stageName}
            </span>
            <span className="text-muted whitespace-nowrap">{s.count} · ${Math.round(s.totalValue).toLocaleString()}</span>
          </div>
          <div className="h-1.5 rounded-full bg-row-odd mt-1 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(s.count / max) * 100}%`, backgroundColor: s.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}


export const WIDGETS: WidgetDef[] = [
  {
    type: 'crm-stat',
    name: 'CRM number',
    description: 'One CRM headline: replies owed, pipeline value, new leads, response time.',
    defaultWidth: 2,
    minWidth: 1,
    options: [CRM_METRIC_OPTION],
    titleOf: (c) => CRM_METRICS.find(m => m.value === (c?.metric ?? 'needsReply'))?.label ?? 'CRM number',
    Component: CrmStatWidget,
  },
  {
    type: 'crm-work',
    name: 'CRM work queue',
    description: 'Due, overdue and undecided — the same counts as My work.',
    defaultWidth: 3,
    minWidth: 2,
    options: [CRM_WORK_SCOPE],
    titleOf: (c) => ((c?.scope ?? 'mine') === 'mine' ? 'My CRM work' : 'CRM work — everyone'),
    Component: CrmWorkWidget,
  },
  {
    type: 'crm-pipeline',
    name: 'Pipeline by stage',
    description: 'Open opportunities per stage, and what each stage is worth.',
    defaultWidth: 3,
    minWidth: 2,
    options: [],
    titleOf: () => 'Pipeline by stage',
    Component: CrmPipelineWidget,
  },

  {
    type: 'stat',
    name: 'Number',
    description: 'A single headline count you can click through. Sits happily at a sixth of the row.',
    defaultWidth: 2,      // a sixth — six across
    minWidth: 1,
    options: [STAT_OPTION],
    titleOf: (c) => STAT_METRICS.find(m => m.value === (c?.metric ?? 'open'))?.label ?? 'Number',
    Component: StatWidget,
  },
  {
    type: 'ticket-list',
    name: 'Ticket list',
    description: 'Tickets matching a preset. Scrolls inside its own height.',
    defaultWidth: 6,      // half
    minWidth: 3,
    defaultHeight: 320,
    options: LIST_OPTIONS,
    titleOf: (c) => LIST_PRESETS.find(p => p.value === (c?.preset ?? 'mine'))?.label ?? 'Tickets',
    Component: TicketListWidget,
  },
  {
    type: 'breakdown',
    name: 'Breakdown chart',
    description: 'How tickets split across statuses or priorities.',
    defaultWidth: 3,      // a quarter
    minWidth: 2,
    options: [BREAKDOWN_OPTION],
    titleOf: (c) => ((c?.by ?? 'status') === 'priority' ? 'By priority' : 'By status'),
    Component: BreakdownWidget,
  },
];

export const widgetDef = (type: string): WidgetDef | undefined =>
  WIDGETS.find(w => w.type === type);

/**
 * The arrangement a user sees before they customise anything. Widths are
 * columns of 12: four stats across the top at a sixth each, then two
 * half-width lists that scroll, with the charts beside them.
 */
export const DEFAULT_LAYOUT = [
  { id: 'd-open',     type: 'stat',        w: 2, config: { metric: 'open' } },
  { id: 'd-mine-n',   type: 'stat',        w: 2, config: { metric: 'mine' } },
  { id: 'd-critical', type: 'stat',        w: 2, config: { metric: 'critical' } },
  { id: 'd-high',     type: 'stat',        w: 2, config: { metric: 'high' } },
  { id: 'd-sla',      type: 'stat',        w: 2, config: { metric: 'sla' } },
  { id: 'd-unassign', type: 'stat',        w: 2, config: { metric: 'unassigned' } },
  { id: 'd-mine',     type: 'ticket-list', w: 6, h: 360, config: { preset: 'mine', limit: '25' } },
  { id: 'd-aged',     type: 'ticket-list', w: 6, h: 360, config: { preset: 'aged', limit: '25' } },
  { id: 'd-status',   type: 'breakdown',   w: 3, config: { by: 'status' } },
  { id: 'd-priority', type: 'breakdown',   w: 3, config: { by: 'priority' } },
];
